// Copyright (c) 2026 SoundDrift. All rights reserved.

// Known UCS CatIDs to strip when formatted as CATID_Description_Vendor_Library (e.g. GLAS_Shatter, AMB_Rain)
const UCS_PREFIXES = /^(?:ALRM|AMBS|AMB|ANML|BELL|BLLT|CART|CERA|CHMS|CLTH|COMP|CRSH|DEST|DIGI|DOOR|DRON|DSGN|ELEC|EXPL|FGHT|FIRE|FOLY|GLAS|GORE|GUNS|HORN|HUMN|IMPT|LASR|LIQD|MAGC|MECH|METL|MOVE|MUSC|OBJT|OBJL|OBJC|PAPR|ROBT|SCIF|SPRT|SWSH|TOON|TOOL|TRNS|VEHC|VOIC|VOXF|VOXM|VOX|WATR|WEAT|WHOO|WIND|WOOD|STOR|TRNE|TRNS)[_ -]+/i;

/**
 * Formats any clip object or clip ID string into a clean, human-readable title that fits UI constraints.
 * Strips raw hash IDs, UCS tags, vendor codes, sample rates, and converts snake/camelCase to Title Case.
 *
 * @param {string|Object} clipOrId - Clip object or raw string ID
 * @param {number} [maxLength=34] - Max length before clean truncation with ellipsis
 * @returns {string} Clean human-readable label
 */
export function formatClipTitle(clipOrId, maxLength = 34) {
  if (!clipOrId) return 'Sound Effect';

  let raw = '';
  if (typeof clipOrId === 'string') {
    raw = clipOrId;
  } else if (typeof clipOrId === 'object') {
    raw = clipOrId.label || clipOrId.title || clipOrId.name || clipOrId.id || clipOrId.clipId || 'Sound Effect';
  }

  if (!raw || typeof raw !== 'string') return 'Sound Effect';

  let s = raw.trim();

  // Strip audio paths & extensions
  s = s.replace(/^audio\//i, '').replace(/\.(opus|mp3|wav|flac|ogg|aiff?)$/i, '');

  // Strip sfx_ prefix
  s = s.replace(/^sfx_/i, '');

  // Check if purely a hex hash/UUID (e.g. 6d296150c14f or 79e6b8b38087)
  if (isHexHash(s)) {
    return 'SFX Sound Clip';
  }

  // Handle Sonniss / UCS filenames with underscore delimiters (e.g. CATID_Description_Vendor_Library_NN)
  if (s.includes('_') || s.includes('-')) {
    const parts = s.split('_').filter(Boolean);
    // If it looks like UCS (e.g. [AMB, RainGentle, TheRecordist, Weather, 02])
    if (parts.length >= 2 && UCS_PREFIXES.test(parts[0])) {
      // The second part is the human description
      s = parts[1];
    } else {
      // Strip UCS prefix if at start
      s = s.replace(UCS_PREFIXES, '');
    }
  } else {
    s = s.replace(UCS_PREFIXES, '');
  }

  // Strip technical sample rate & bit depth suffixes (e.g. 96k, 48k, 44k, 24b, 16b, Stereo, Mono)
  s = s.replace(/\b(96k|48k|44\.1k|44k|24b|16b|32b|stereo|mono)\b/gi, '');

  // Strip trailing take/version numbers (e.g. _01, _02, -1, take 1, v1)
  s = s.replace(/[-_ ]+(0\d|\d{1,3}|take\s*\d+|v\d+)$/i, '');

  // Convert camelCase or PascalCase to spaced words (e.g. RainGentle -> Rain Gentle, SubwayStation -> Subway Station)
  s = s.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Replace underscores and hyphens with spaces
  s = s.replace(/[-_]+/g, ' ');

  // Remove multiple spaces
  s = s.replace(/\s+/g, ' ').trim();

  // Capitalize words nicely
  const words = s.split(' ').filter(Boolean);
  if (words.length === 0) return 'Sound Effect';

  let formatted = words
    .map(w => {
      // Keep short acronyms uppercase (e.g. SFX, UI, TV, DJ, UFO, RPG, NPC)
      if (/^(sfx|ui|tv|dj|ufo|rpg|npc|fbi|cia|sci-fi)$/i.test(w)) return w.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');

  // If too long, truncate cleanly with ellipsis
  if (maxLength && formatted.length > maxLength) {
    formatted = formatted.slice(0, maxLength - 1).trim() + '…';
  }

  return formatted || 'Sound Effect';
}

function isHexHash(str) {
  if (!str) return false;
  const s = str.trim().replace(/^sfx_/i, '');
  // Matches hex hashes of length 8-64 or alphanumeric GUIDs without real words
  return /^[0-9a-f]{8,64}$/i.test(s);
}

export default formatClipTitle;
